import { markRaw } from 'vue';
import { throttle } from 'lodash';
import api from './api';
import router from '../../router';

//Path Regex to check if socket listener should be used
const pathRegex = /^\/core/;

const state = {
	loading: false,
	error: false,
	message: '',
	planningLogLimit: 1000,
	status: {},
	publishedStatus: {},
	fullReloadStatus: false,
	newPlanningLog: null,
};

const getters = {};

const mutations = {
	startLoading(state) {
		state.loading = true;
	},
	stopLoading(state) {
		state.loading = false;
		state.error = false;
	},
	setError(state, { message }) {
		state.loading = false;
		state.error = true;
		state.message = message;
	},
	setStatus(state, { status }) {
		if (status?.results?.driverResults?.length) {
			const driverResults = status.results.driverResults.map((driverResult) => ({
				...driverResult,
				preferencesTotal:
					driverResult.PreferenceRate_rate_absences + driverResult.PreferenceRate_rate_shiftsAndBreaks + driverResult.PreferenceRate_rate_general,
			}));
			status.results.driverResults = driverResults;
		}

		if (status?.results) {
			markRaw(status.results);
		}

		state.status = markRaw(status);
	},
	setPublishedStatus(state, { status }) {
		state.publishedStatus = status.published;
	},
	setFullReloadStatus(state, { status }) {
		state.fullReloadStatus = status;
	},
	setStarting(state) {
		state.status.process.starting = true;
	},
	setStopping(state) {
		state.status.process.stopping = true;
	},
	setPopulation(state, { population }) {
		state.status.roster.population = population;
	},
	setNewPlanningLog(state, planningLog) {
		state.newPlanningLog = markRaw(planningLog);
	},
};

const throttledLoadStatus = throttle(
	(dispatch) => {
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}
		dispatch('loadStatus');
	},
	750,
	{ leading: true, trailing: true }
);

const actions = {
	async loadPlanningLogs({ state, commit, rootGetters, dispatch }) {
		commit('startLoading');

		try {
			const planningLogs = (await api.loadPlanningLogs(state.planningLogLimit)).data;
			commit('stopLoading');
			// return planningLogs;
			return planningLogs.map((log) => markRaw(log));
		} catch (err) {
			if (err && err.response && err.response.data) {
				commit('setError', { message: err.response.data.message });
			}
			return [];
		}
	},

	async loadStatus({ state, commit, rootGetters, dispatch }) {
		commit('startLoading');
		try {
			const status = (await api.getStatus()).data;
			commit('setStatus', { status });
			commit('setPublishedStatus', { status });
			commit('stopLoading');
		} catch (err) {
			if (err && err.response && err.response.data) {
				commit('setError', { message: err.response.data.message });
			}
		}
	},

	async setRunning({ state, commit }, running) {
		try {
			if (running) {
				commit('setStarting', {});
			} else {
				commit('setStopping', {});
			}
			const result = await api.setRunning(running);
			return result && result.status == 202;
		} catch (err) {
			if (err && err.response && err.response.data) {
				commit('setError', { message: err.response.data.message });
			}
		}
		return false;
	},

	async resetRoster({ state, commit }) {
		try {
			await api.resetRoster();
		} catch (err) {
			if (err && err.response && err.response.data) {
				commit('setError', { message: err.response.data.message });
			}
		}
	},

	async evaluate({ state, commit }) {
		try {
			await api.evaluate();
		} catch (err) {
			if (err && err.response && err.response.data) {
				commit('setError', { message: err.response.data.message });
			}
		}
	},

	async evaluatePlanning({ state, commit }) {
		try {
			await api.evaluatePlanning();
		} catch (err) {
			if (err && err.response && err.response.data) {
				commit('setError', { message: err.response.data.message });
			}
		}
	},

	async triggerFullReload({ state, commit }) {
		try {
			await api.fullReload();
		} catch (err) {
			if (err && err.response && err.response.data) {
				commit('setError', { message: err.response.data.message });
			}
		}
	},

	async clearCache({ state, commit }) {
		try {
			await api.clearCache();
		} catch (err) {
			if (err && err.response && err.response.data) {
				commit('setError', { message: err.response.data.message });
			}
		}
	},

	socket_created_PlanningLog({ dispatch, commit }, payload) {
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}

		if (payload.length && payload[0].payload) {
			commit('setNewPlanningLog', payload[0].payload);
		}
		throttledLoadStatus(dispatch);
	},

	socket_planning_status({ dispatch, commit, state }, payload) {
		const status = payload[0].payload;
		commit('setStatus', { status });
		if (!status.process.evaluating && !state.publishedStatus.evaluating) {
			commit('setFullReloadStatus', { status: false });
		}
	},

	socket_published_status({ dispatch, commit }, payload) {
		const status = payload[0].payload;
		commit('setPublishedStatus', { status });
	},

	socket_fullReloadStarted({ dispatch, commit }, payload) {
		commit('setFullReloadStatus', { status: true });
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
